import type { ShipmentRow } from '#/entities/shipment'
import {
  LINE_CAPABILITY,
  isImessageProvider,
  useDefaultLine,
} from '#/entities/telephony'
import type { MassRecipientInput } from '#/shared/api/generated/communication/models'
import { useWorkspaceTabState } from '#/shared/lib/workspace-tabs'

import type { MassSmsViewModel } from '../lib/mass-sms'

export interface UseMassSmsActionResult {
  open: boolean
  openSms: () => void
  onOpenChange: (open: boolean) => void
  viewModel: MassSmsViewModel
}

function toSmsRecipients(
  rows: ShipmentRow[],
  selectedIds: string[],
): MassRecipientInput[] {
  const selected = new Set(selectedIds)
  return rows
    .filter((row) => selected.has(row.id) && row.customerPhone)
    .map((row) => ({
      contact: row.customerPhone,
      name: row.customerName || null,
      shipment_id: row.shipmentId,
      customer_id: row.customerId || null,
    }))
}

export function useMassSmsAction(
  rows: ShipmentRow[],
  selectedIds: string[],
): UseMassSmsActionResult {
  // Per workspace tab, so a mass-text left open comes back when the user
  // switches to another workspace tab and returns.
  const [open, setOpen] = useWorkspaceTabState('massSmsOpen', false)
  // Share the one default-line selection so a bulk text starts from the same
  // line as a call; the user can still override it in the dialog.
  const { lines, line } = useDefaultLine({
    capability: LINE_CAPABILITY.sms,
    enabled: open,
  })
  return {
    open,
    openSms: () => setOpen(true),
    onOpenChange: setOpen,
    viewModel: {
      fromNumbers: lines.map((entry) => ({
        id: entry.id,
        phoneNumber: entry.phone_number,
        isImessage: isImessageProvider(entry.provider),
      })),
      recipients: toSmsRecipients(rows, selectedIds),
      defaultFromNumberId: line?.id,
      // A blast to a selected list is promotional traffic — the pre-send check
      // holds it to the opt-out rules and flags a body with no "Reply STOP".
      isMarketing: true,
    },
  }
}
